<script setup lang="ts">
  import { ref, onMounted, watch, onUnmounted, defineAsyncComponent, computed } from 'vue'
  import { useCommonStore, useFeaturesStore, useTravelersStore, useUserStore } from '@/shared/store'
  import type { TravelerInformation as ApiTravelerInformation } from '@/shared/types/api.types'
  import { HOLDER_ID } from '@/widgets/Flow/FlowInsuredTravelersWidgetQC/FlowInsuredTravelersWidgetQC.constants'
  import {
    useApiRequestsAbort,
    useWinterSport,
    useGoogleAnalytics,
    usePerformanceTiming,
  } from '@/shared/composables'
  import { TravelDirection } from '@/shared/types/common.types'
  import { useFormDataWithRedirect } from '@/shared/composables/useFormDataWithRedirect'

  withDefaults(defineProps<Props>(), {
    loading: false,
    withBackButton: false,
  })
  const emit = defineEmits<Emits>()

  const FlowInsuredTravelersWidget = defineAsyncComponent(
    () => import('@/widgets/Flow/FlowInsuredTravelersWidget/FlowInsuredTravelersWidget.vue')
  )
  const FlowTravelerFormWidget = defineAsyncComponent(
    () => import('@/widgets/Flow/FlowTravelerFormWidget/FlowTravelerFormWidget.vue')
  )
  const FlowPersonalInformationWidgetQC = defineAsyncComponent(
    () =>
      import('@/widgets/Flow/FlowPersonalInformationWidgetQC/FlowPersonalInformationWidgetQC.vue')
  )
  const FlowPersonalInformationWidget = defineAsyncComponent(
    () => import('@/widgets/Flow/FlowPersonalInformationWidget/FlowPersonalInformationWidget.vue')
  )
  const FlowInsuredTravelersWidgetQC = defineAsyncComponent(
    () => import('@/widgets/Flow/FlowInsuredTravelersWidgetQC/FlowInsuredTravelersWidgetQC.vue')
  )
  const FlowTravelerFormWidgetQC = defineAsyncComponent(
    () => import('@/widgets/Flow/FlowTravelerFormWidgetQC/FlowTravelerFormWidgetQC.vue')
  )

  interface Props {
    loading?: boolean
    withBackButton?: boolean
  }

  const featuresStore = useFeaturesStore()
  const isInsuredTravelersQcEnabled = computed(() => featuresStore.isInsuredTravelersQcEnabled)
  const isPersonalInformationQcEnabled = computed(
    () => featuresStore.isPersonalInformationQcEnabled
  )

  interface Emits {
    (e: 'proceed'): void
    (e: 'load', request: () => Promise<any>, requestId?: string, withLoader?: boolean): void
    (e: 'back'): void
  }
  useApiRequestsAbort()

  const { getFormData } = useFormDataWithRedirect()
  const travelersStore = useTravelersStore()
  const commonStore = useCommonStore()
  const userStore = useUserStore()
  const { withWinterSport } = useWinterSport()
  // const { withWinterSport, fetchWinterSportPrice: _fetchWinterSportPrice } = useWinterSport();
  const gaService = useGoogleAnalytics()
  const { onPerformanceCheckEnd } = usePerformanceTiming()

  const isTravelerFormActive = ref(false)
  const isHolderFormActive = ref(false)
  const travelerInformation = ref<ApiTravelerInformation | null>(null)
  const travelersInformation = ref<ApiTravelerInformation[]>([])
  const winterSportPrice = ref(0)
  const loadingTravelerForm = ref(false)

  const onProceed = () => {
    emit('proceed')
  }

  const fetchTravellers = async () => {
    travelersInformation.value = await travelersStore.getTravelers()
  }

  // const fetchWinterSportPrice = async () => {
  //   if (withWinterSport) {
  //     winterSportPrice.value = await _fetchWinterSportPrice();
  //   }
  // };

  const onDelete = async (id: string) => {
    await emit('load', async () => {
      await travelersStore.deleteTraveler(id)
      await fetchTravellers()
    })
  }

  const onEdit = (id: string) => {
    userStore.setActiveInsuredTravelersSubPage('edit')
    if (id === HOLDER_ID) {
      isHolderFormActive.value = true
      return
    }
    const possibleTravelerToEdit = travelersInformation.value.find(
      (traveler) => String(traveler.srNo) === id
    )
    if (!possibleTravelerToEdit) return new Error('no traveler to edit')
    travelerInformation.value = possibleTravelerToEdit
    isTravelerFormActive.value = true
    fetchRelations()
  }

  const onAdd = () => {
    userStore.setActiveInsuredTravelersSubPage('add')
    travelerInformation.value = null
    isTravelerFormActive.value = true
    fetchRelations()
  }

  const saveTraveler = async (traveler: ApiTravelerInformation) => {
    await emit(
      'load',
      async () => {
        loadingTravelerForm.value = true
        await travelersStore.saveTraveler(traveler)
        await fetchTravellers()
        loadingTravelerForm.value = false
        isTravelerFormActive.value = false
        isHolderFormActive.value = false

        if (userStore.activeInsuredTravelersSubPage === 'add') {
          gaService.sendInsuredTravelersAddNew(
            TravelDirection.OUTBOUND,
            traveler.relationName,
            traveler.nationalityName
          )
        } else {
          gaService.sendInsuredTravelersUpdateProceed(
            TravelDirection.OUTBOUND,
            traveler.nationalityName,
            traveler.relationName
          )
        }
      },
      'saveTraveler',
      !isPersonalInformationQcEnabled.value
    )
  }

  const fetchNationalities = async () => {
    if (commonStore.nationalities.length > 0) return

    const data = await commonStore.findNationalities()
    commonStore.setNationalities(data)
  }

  const fetchRelations = async () => {
    if (commonStore.relations.length > 0) return

    const data = await commonStore.getRelations()
    commonStore.setRelations(data)
  }

  onMounted(() => {
    emit('load', async () => {
      if (!(await getFormData(TravelDirection.OUTBOUND, true))) return
      await Promise.all([fetchTravellers(), fetchNationalities()])
      onPerformanceCheckEnd()
    })
  })

  // TODO: Refactor the way of handling go back
  watch(
    () => isTravelerFormActive.value,
    (newValue) => {
      commonStore.setStopBack(newValue)

      if (!newValue) {
        commonStore.setBackCallback(null)
        userStore.setActiveInsuredTravelersSubPage(undefined)
      } else {
        commonStore.setBackCallback(() => (isTravelerFormActive.value = false))
      }
    }
  )

  watch(
    () => isHolderFormActive.value,
    (newValue) => {
      commonStore.setStopBack(newValue)

      if (!newValue) {
        commonStore.setBackCallback(null)
        userStore.setActiveInsuredTravelersSubPage(undefined)
      } else {
        commonStore.setBackCallback(() => (isHolderFormActive.value = false))
      }
    }
  )

  onUnmounted(() => {
    commonStore.setStopBack(false)
    commonStore.setBackCallback(null)
    userStore.setActiveInsuredTravelersSubPage(undefined)
  })
</script>

<template>
  <div v-show="isInsuredTravelersQcEnabled && !loading">
    <FlowTravelerFormWidgetQC
      v-if="isTravelerFormActive && isPersonalInformationQcEnabled"
      :information="travelerInformation"
      :with-winter-sport="withWinterSport"
      :loading="loadingTravelerForm"
      :winter-sport-price="winterSportPrice"
      :with-back-button="withBackButton"
      @save="saveTraveler"
      @back="emit('back')"
    />

    <FlowTravelerFormWidget
      v-else-if="isTravelerFormActive"
      :information="travelerInformation"
      :with-winter-sport="withWinterSport"
      :winter-sport-price="winterSportPrice"
      @save="saveTraveler"
    />

    <template v-else-if="isHolderFormActive">
      <FlowPersonalInformationWidgetQC
        v-if="isPersonalInformationQcEnabled"
        :with-winter-sport="false"
        :winter-sport-price="winterSportPrice"
        is-edit
        :with-back-button="withBackButton"
        @load="emit('load', $event, 'personal-information', false)"
        @proceed="isHolderFormActive = false"
        @back="emit('back')"
      />

      <FlowPersonalInformationWidget
        v-else
        :with-winter-sport="false"
        :winter-sport-price="winterSportPrice"
        is-edit
        @load="emit('load', $event)"
        @proceed="isHolderFormActive = false"
      />
    </template>

    <FlowInsuredTravelersWidgetQC
      v-else
      :travelers="travelersInformation"
      :with-back-button="withBackButton"
      @add="onAdd"
      @edit="onEdit"
      @delete="onDelete"
      @proceed="onProceed"
      @back="emit('back')"
    />
  </div>

  <div v-show="!isInsuredTravelersQcEnabled && !loading">
    <FlowTravelerFormWidgetQC
      v-if="isTravelerFormActive && isPersonalInformationQcEnabled"
      :information="travelerInformation"
      :with-winter-sport="withWinterSport"
      :loading="loadingTravelerForm"
      :winter-sport-price="winterSportPrice"
      :with-back-button="withBackButton"
      @save="saveTraveler"
      @back="emit('back')"
    />

    <FlowTravelerFormWidget
      v-else-if="isTravelerFormActive"
      :information="travelerInformation"
      :with-winter-sport="withWinterSport"
      :winter-sport-price="winterSportPrice"
      @save="saveTraveler"
    />

    <template v-else-if="isHolderFormActive">
      <FlowPersonalInformationWidgetQC
        v-if="isPersonalInformationQcEnabled"
        :with-winter-sport="false"
        :winter-sport-price="winterSportPrice"
        is-edit
        :with-back-button="withBackButton"
        @load="emit('load', $event, 'personal-information', false)"
        @proceed="isHolderFormActive = false"
        @back="emit('back')"
      />

      <FlowPersonalInformationWidget
        v-else
        :with-winter-sport="false"
        :winter-sport-price="winterSportPrice"
        is-edit
        @load="emit('load', $event)"
        @proceed="isHolderFormActive = false"
      />
    </template>

    <FlowInsuredTravelersWidget
      v-else
      :travelers="travelersInformation"
      @add="onAdd"
      @edit="onEdit"
      @delete="onDelete"
      @proceed="onProceed"
    />
  </div>
</template>
